import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, useParams } from 'react-router';
import styled from 'styled-components';
import { Divider } from 'ui/divider';
import { Toggle, TOGGLE_SIZES } from 'ui/toggle';
import { ConfirmModal } from 'ui/modal';
import { BUTTON_VARIANTS } from 'ui/button';
import { PageHeader } from 'ui/pageHeader';
import { Spinner } from 'ui/spinner';
import { Link } from 'ui/links';
import { selectIsSMSLinkShorteningFeatureFlagOn } from 'core/userContext/selectors';

import { AnnotatedSection, Annotation, SectionContent } from 'ui-beta/annotatedSection';

import { CNAME } from './CNAME';
import { Paths } from './Paths';

import { BREADCRUMB } from '../constants';

import {
  fetchMailDomainsRequest,
  editTrackingDomainRequest,
  fetchDeepLinksRequest,
} from '../actions';

import {
  getIsLoading,
  selectAppSiteAssociationsByTrackingDomain,
  selectTrackingDomainFromDomainName,
} from '../selectors';

import { Container, EmptyStateContainer } from './MailDomains.styled';

import { StyledLabel, FlexContainer, StyledDescription } from './TrackingDomain.styled';

const Paragraph = styled.div`
  margin-top: 10px;
`;

const TEXT = {
  domainTitle: 'Domain',
  domainDescription:
    "Tracking domains track clicks, set attribution information and redirect to a link's intended destination",
  domainInputLabel: 'Tracking domain',
  verificationTitle: 'Verification',
  verificationText:
    'To improve deliverability, tracking domains must be verfied by an associated CNAME record or an HTTPS proxy server (for example, Amazon CloudFront or Cloudflare).',
  verificationLabel: 'Enable HTTPS',
  deepLinksTitle: 'Deep links',
};

export const TrackingDomain = () => {
  const dispatch = useDispatch();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { trackingDomainName } = useParams();
  const isLoading = useSelector(getIsLoading);
  const isLinkShorteningFeatureFlagOn = useSelector(selectIsSMSLinkShorteningFeatureFlagOn);

  const trackingDomain = useSelector((state) =>
    selectTrackingDomainFromDomainName(state, { trackingDomainName }),
  );
  const appAssociationSites = useSelector((state) =>
    selectAppSiteAssociationsByTrackingDomain(state, { trackingDomainName }),
  );

  const { useHttps } = trackingDomain;

  const setHttps = (value) => {
    dispatch(
      editTrackingDomainRequest({
        ...trackingDomain,
        useHttps: value,
      }),
    );
    setModalIsOpen(false);
  };

  const httpsRadioAction = () => (useHttps ? setModalIsOpen(true) : setHttps(true));

  useEffect(() => {
    if (!trackingDomain.domain) {
      dispatch(fetchMailDomainsRequest());
    }
  }, [trackingDomain, dispatch]);

  useEffect(() => {
    dispatch(fetchDeepLinksRequest());
  }, [dispatch]);

  if (isLoading) {
    return (
      <EmptyStateContainer>
        <Spinner />
      </EmptyStateContainer>
    );
  }

  return (
    <>
      <Container>
        <PageHeader breadcrumb={BREADCRUMB(isLinkShorteningFeatureFlagOn)}>
          Tracking Domain - {decodeURIComponent(trackingDomainName)}
        </PageHeader>
        <AnnotatedSection breakpoint={1000}>
          <Annotation title={TEXT.verificationTitle}>{TEXT.verificationText}</Annotation>
          <SectionContent>
            <FlexContainer>
              <div>
                <StyledLabel>{TEXT.verificationLabel}</StyledLabel>
                <StyledDescription>
                  By default, Iterable uses HTTP with link tracking domains. To use HTTPS,&nbsp;
                  <Link
                    href="https://support.iterable.com/hc/en-us/articles/115000427446-How-to-set-up-HTTPS-for-link-click-tracking"
                    alt="How-to-set-up-HTTPS-for-link-click-tracking"
                  >
                    follow these instructions
                  </Link>
                  .
                </StyledDescription>
              </div>
              <Toggle
                data-test="mailDomains-edit-tracking-domain-use-https"
                onClick={httpsRadioAction}
                isActive={useHttps}
                size={TOGGLE_SIZES.LARGE}
                id="mailDomains-edit-tracking-domain-use-https"
              />
            </FlexContainer>
            {!useHttps && trackingDomain.domain && <CNAME trackingDomain={trackingDomain} />}
          </SectionContent>
        </AnnotatedSection>
        <Divider />
        <AnnotatedSection breakpoint={1000}>
          <Annotation title={TEXT.deepLinksTitle}>
            Mobile devices open email and SMS deep links in your app (if it&apos;s installed) or a
            web browser.
            <Paragraph>
              Read our docs to learn about deep linking with{' '}
              <Link href="https://support.iterable.com/hc/en-us/articles/360024366291-Integrating-Iterable-with-AppsFlyer">
                AppsFlyer
              </Link>{' '}
              and{' '}
              <Link href="https://support.iterable.com/hc/en-us/articles/360018347731-Configure-Iterable-for-Branch">
                Branch
              </Link>
              .
            </Paragraph>
          </Annotation>
          <SectionContent>
            <Paths appAssociationSites={appAssociationSites} trackingDomain={trackingDomain} />
          </SectionContent>
        </AnnotatedSection>
      </Container>
      {modalIsOpen && (
        <ConfirmModal
          bodyContent="Disabling HTTPS will also disable any configured deep links. Would you like to continue?"
          confirmButtonContent="Disable"
          confirmButtonVariant={BUTTON_VARIANTS.DESTRUCTIVE}
          headerContent="Disable HTTPS"
          id="turn-off-https-modal"
          isModalOpen={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
          onConfirm={() => setHttps(false)}
        />
      )}
    </>
  );
};

export default withRouter(TrackingDomain);
